// ============================================
// POSITION TRACKER SERVICE
// Client-side price polling & alert management
// ============================================

import { ActivePosition, PositionSummary, TrailingStopConfig } from '../types';
import { calculateTrailingStop } from './strategyEngine';

const API_BASE = '/api/positions';

// ============================================
// US MARKET HOURS & HOLIDAYS
// ============================================

// US Stock Market holidays for 2025-2026 (NYSE/NASDAQ closed)
const US_MARKET_HOLIDAYS = [
  // 2025
  '2025-01-01', // New Year's Day
  '2025-01-20', // MLK Day
  '2025-02-17', // Presidents Day
  '2025-04-18', // Good Friday
  '2025-05-26', // Memorial Day
  '2025-06-19', // Juneteenth
  '2025-07-04', // Independence Day
  '2025-09-01', // Labor Day
  '2025-11-27', // Thanksgiving
  '2025-12-25', // Christmas
  // 2026
  '2026-01-01', // New Year's Day
  '2026-01-19', // MLK Day
  '2026-02-16', // Presidents Day
  '2026-04-03', // Good Friday
  '2026-05-25', // Memorial Day
  '2026-06-19', // Juneteenth
  '2026-07-03', // Independence Day (observed)
  '2026-09-07', // Labor Day
  '2026-11-26', // Thanksgiving
  '2026-12-25', // Christmas
];

/**
 * Check if US is currently observing Daylight Saving Time
 * DST starts: Second Sunday in March at 2:00 AM local
 * DST ends: First Sunday in November at 2:00 AM local
 */
function isUSDaylightSavingTime(date: Date): boolean {
  const year = date.getUTCFullYear();

  // Find second Sunday in March
  const march = new Date(Date.UTC(year, 2, 1)); // March 1st
  const marchSecondSunday = new Date(march);
  // Find first Sunday
  const daysUntilSundayMarch = (7 - march.getUTCDay()) % 7;
  marchSecondSunday.setUTCDate(1 + daysUntilSundayMarch + 7); // Second Sunday
  marchSecondSunday.setUTCHours(7, 0, 0, 0); // 2 AM EST = 7 AM UTC

  // Find first Sunday in November
  const november = new Date(Date.UTC(year, 10, 1)); // November 1st
  const novemberFirstSunday = new Date(november);
  const daysUntilSundayNov = (7 - november.getUTCDay()) % 7;
  novemberFirstSunday.setUTCDate(1 + (daysUntilSundayNov === 0 ? 0 : daysUntilSundayNov));
  novemberFirstSunday.setUTCHours(6, 0, 0, 0); // 2 AM EDT = 6 AM UTC

  return date >= marchSecondSunday && date < novemberFirstSunday;
}

/**
 * Get US market hours in UTC based on DST
 * US Market Hours: 9:30 AM - 4:00 PM Eastern Time
 * - EST (Winter): UTC-5, so 14:30 - 21:00 UTC
 * - EDT (Summer): UTC-4, so 13:30 - 20:00 UTC
 */
function getMarketHoursUTC(date: Date): { openMinutes: number; closeMinutes: number } {
  const isDST = isUSDaylightSavingTime(date);

  if (isDST) {
    // EDT: UTC-4
    return {
      openMinutes: 13 * 60 + 30,   // 13:30 UTC = 9:30 AM EDT
      closeMinutes: 20 * 60        // 20:00 UTC = 4:00 PM EDT
    };
  } else {
    // EST: UTC-5
    return {
      openMinutes: 14 * 60 + 30,   // 14:30 UTC = 9:30 AM EST
      closeMinutes: 21 * 60        // 21:00 UTC = 4:00 PM EST
    };
  }
}

/**
 * Check if the market is currently open
 * US Market Hours: 9:30 AM - 4:00 PM ET (adjusts for DST)
 * Only Monday-Friday, excluding US holidays
 */
function isMarketOpen(): boolean {
  const now = new Date();

  // Get current day of week (0=Sunday, 6=Saturday)
  const dayOfWeek = now.getUTCDay();

  // Skip weekends
  if (dayOfWeek === 0 || dayOfWeek === 6) {
    return false;
  }

  // Check for US holidays
  const todayStr = now.toISOString().split('T')[0];
  if (US_MARKET_HOLIDAYS.includes(todayStr)) {
    return false;
  }

  // Check market hours (adjusts for DST)
  const hours = now.getUTCHours();
  const minutes = now.getUTCMinutes();
  const timeInMinutes = hours * 60 + minutes;

  const { openMinutes, closeMinutes } = getMarketHoursUTC(now);

  return timeInMinutes >= openMinutes && timeInMinutes < closeMinutes;
}

/**
 * Get next market open time for display purposes
 */
function getNextMarketOpen(): Date {
  const now = new Date();
  const next = new Date(now);

  // Get current market hours
  const { closeMinutes } = getMarketHoursUTC(now);
  const currentMinutes = now.getUTCHours() * 60 + now.getUTCMinutes();

  // If it's after market close, move to next day
  if (currentMinutes >= closeMinutes) {
    next.setUTCDate(next.getUTCDate() + 1);
  }

  // Skip to Monday if weekend
  let dayOfWeek = next.getUTCDay();
  if (dayOfWeek === 0) next.setUTCDate(next.getUTCDate() + 1); // Sunday -> Monday
  if (dayOfWeek === 6) next.setUTCDate(next.getUTCDate() + 2); // Saturday -> Monday

  // Skip holidays (and re-check for weekends after skipping)
  let nextStr = next.toISOString().split('T')[0];
  while (US_MARKET_HOLIDAYS.includes(nextStr) || next.getUTCDay() === 0 || next.getUTCDay() === 6) {
    next.setUTCDate(next.getUTCDate() + 1);
    nextStr = next.toISOString().split('T')[0];
  }

  // Set to market open time (adjusted for DST on that date)
  const { openMinutes } = getMarketHoursUTC(next);
  const openHours = Math.floor(openMinutes / 60);
  const openMins = openMinutes % 60;
  next.setUTCHours(openHours, openMins, 0, 0);

  return next;
}

// ============================================
// API METHODS
// ============================================

export async function fetchPositions(clerkId: string, status?: string): Promise<ActivePosition[]> {
  const params = new URLSearchParams({ clerkId });
  if (status) params.append('status', status);

  const res = await fetch(`${API_BASE}?action=list&${params}`);
  const data = await res.json();

  if (!data.success) throw new Error(data.error || 'Failed to fetch positions');
  return data.positions;
}

export async function fetchPosition(clerkId: string, positionId: number): Promise<ActivePosition> {
  const params = new URLSearchParams({ clerkId, positionId: String(positionId) });
  const res = await fetch(`${API_BASE}?action=get&${params}`);
  const data = await res.json();

  if (!data.success) throw new Error(data.error || 'Failed to fetch position');
  return data.position;
}

export async function createPosition(clerkId: string, position: Partial<ActivePosition>): Promise<ActivePosition> {
  const res = await fetch(`${API_BASE}?action=create`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ clerkId, position })
  });
  const data = await res.json();

  if (!data.success) throw new Error(data.error || 'Failed to create position');
  return data.position;
}

export async function updatePosition(
  clerkId: string,
  positionId: number,
  updates: Partial<ActivePosition>
): Promise<ActivePosition> {
  const res = await fetch(`${API_BASE}?action=update`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ clerkId, positionId, updates })
  });
  const data = await res.json();

  if (!data.success) throw new Error(data.error || 'Failed to update position');
  return data.position;
}

export async function closePosition(
  clerkId: string,
  positionId: number,
  closedPrice?: number,
  closedPnL?: number,
  closeReason?: string,
  status?: string
): Promise<void> {
  const res = await fetch(`${API_BASE}?action=close`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ clerkId, positionId, closedPrice, closedPnL, closeReason, status })
  });
  const data = await res.json();

  if (!data.success) throw new Error(data.error || 'Failed to close position');
}

export async function deletePosition(clerkId: string, positionId: number): Promise<void> {
  const res = await fetch(`${API_BASE}?action=delete`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ clerkId, positionId })
  });
  const data = await res.json();

  if (!data.success) throw new Error(data.error || 'Failed to delete position');
}

export async function fetchPositionSummary(clerkId: string): Promise<PositionSummary> {
  const params = new URLSearchParams({ clerkId });
  const res = await fetch(`${API_BASE}?action=summary&${params}`);
  const data = await res.json();

  if (!data.success) throw new Error(data.error || 'Failed to fetch summary');
  return data.summary;
}

// ============================================
// PRICE POLLING SERVICE
// ============================================

interface PricePollingCallbacks {
  onPriceUpdate?: (position: ActivePosition, newPrice: number, pnl: number, pnlPercent: number) => void;
  onStopHit?: (position: ActivePosition, currentPrice: number) => void;
  onProfitTarget?: (position: ActivePosition, targetPercent: 50 | 100, currentPrice: number) => void;
  onTrailingStopUpdate?: (position: ActivePosition, newStopPrice: number) => void;
  onDTEWarning?: (position: ActivePosition, daysRemaining: number) => void;
  onMarketClosed?: (nextOpen: Date) => void;
  onMarketOpen?: () => void;
  onError?: (error: Error, position?: ActivePosition) => void;
}

class PositionPollingService {
  private intervalId: number | null = null;
  private positions: ActivePosition[] = [];
  private callbacks: PricePollingCallbacks = {};
  private clerkId: string | null = null;
  private pollingIntervalMs = 5 * 60 * 1000; // 5 minutes default
  private wasMarketOpen = false; // Track market state transitions
  private lastMarketCheck: Date | null = null;

  start(clerkId: string, callbacks: PricePollingCallbacks, intervalMs?: number) {
    this.clerkId = clerkId;
    this.callbacks = callbacks;
    if (intervalMs) this.pollingIntervalMs = intervalMs;

    // Initial fetch
    this.pollPrices();

    // Set up interval
    this.intervalId = window.setInterval(() => {
      this.pollPrices();
    }, this.pollingIntervalMs);

    console.log(`[PositionService] Started polling every ${this.pollingIntervalMs / 1000}s`);
  }

  stop() {
    if (this.intervalId) {
      window.clearInterval(this.intervalId);
      this.intervalId = null;
    }
    console.log('[PositionService] Stopped polling');
  }

  isMarketCurrentlyOpen(): boolean {
    return isMarketOpen();
  }

  getNextMarketOpenTime(): Date {
    return getNextMarketOpen();
  }

  async pollPrices() {
    if (!this.clerkId) return;

    // Check market hours - only poll during trading hours
    const marketOpen = isMarketOpen();
    this.lastMarketCheck = new Date();

    // Handle market state transitions
    if (!marketOpen && this.wasMarketOpen) {
      // Market just closed
      console.log('[PositionService] Market closed - pausing price updates');
      this.callbacks.onMarketClosed?.(getNextMarketOpen());
    } else if (marketOpen && !this.wasMarketOpen) {
      // Market just opened
      console.log('[PositionService] Market open - resuming price updates');
      this.callbacks.onMarketOpen?.();
    }

    this.wasMarketOpen = marketOpen;

    // Skip price fetching if market is closed
    if (!marketOpen) {
      console.log(`[PositionService] Market closed - skipping poll. Next open: ${getNextMarketOpen().toISOString()}`);
      return;
    }

    try {
      // Fetch active positions
      this.positions = await fetchPositions(this.clerkId, 'ACTIVE');

      if (this.positions.length === 0) return;

      // Get unique tickers
      const tickers = [...new Set(this.positions.map(p => p.ticker))];

      // Fetch current prices for all tickers (batch if needed)
      const prices = await this.fetchCurrentPrices(tickers);

      // Update each position
      for (const position of this.positions) {
        try {
          await this.updatePositionPrice(position, prices[position.ticker]);
        } catch (err) {
          this.callbacks.onError?.(err as Error, position);
        }
      }
    } catch (err) {
      this.callbacks.onError?.(err as Error);
    }
  }

  private async fetchCurrentPrices(tickers: string[]): Promise<Record<string, number>> {
    const prices: Record<string, number> = {};

    // Fetch from Finnhub (free tier: 60 calls/min)
    for (const ticker of tickers) {
      try {
        const res = await fetch(`/api/market-data?action=quote&symbol=${ticker}`);
        const data = await res.json();
        if (data.c) { // Finnhub returns 'c' for current price
          prices[ticker] = data.c;
        }
      } catch (err) {
        console.error(`[PositionService] Failed to fetch price for ${ticker}:`, err);
      }
    }

    return prices;
  }

  private async updatePositionPrice(position: ActivePosition, currentStockPrice: number | undefined) {
    if (!currentStockPrice || !this.clerkId) return;

    // Calculate current spread price (simplified - for accurate pricing you'd need option chain)
    // For now, estimate based on delta/stock movement from entry
    const stockMove = currentStockPrice - (position.legs[0]?.strike || currentStockPrice);
    const estimatedSpreadPrice = this.estimateSpreadPrice(position, currentStockPrice);

    // Calculate P&L
    const pnl = this.calculatePnL(position, estimatedSpreadPrice);
    const pnlPercent = position.maxProfit > 0 ? (pnl / position.maxProfit) * 100 : 0;

    // Update trailing stop if enabled
    let trailingStopPrice = position.trailingStopPrice;
    if (position.trailingStopEnabled && position.atrValue) {
      const trailingConfig = calculateTrailingStop(
        position.entryPrice,
        estimatedSpreadPrice,
        position.atrValue,
        position.direction === 'BEARISH' ? 'BEARISH' : 'BULLISH',
        position.maxProfit
      );
      trailingStopPrice = trailingConfig.currentStopPrice;

      if (trailingStopPrice !== position.trailingStopPrice) {
        this.callbacks.onTrailingStopUpdate?.(position, trailingStopPrice);
      }
    }

    // Check stop loss
    if (this.isStopHit(position, estimatedSpreadPrice, trailingStopPrice)) {
      this.callbacks.onStopHit?.(position, estimatedSpreadPrice);
    }

    // Check profit targets
    if (pnlPercent >= 100 && position.currentPnLPercent && position.currentPnLPercent < 100) {
      this.callbacks.onProfitTarget?.(position, 100, estimatedSpreadPrice);
    } else if (pnlPercent >= 50 && position.currentPnLPercent && position.currentPnLPercent < 50) {
      this.callbacks.onProfitTarget?.(position, 50, estimatedSpreadPrice);
    }

    // Check DTE warning (14 days)
    const daysToExpiry = this.getDaysToExpiry(position);
    if (daysToExpiry <= 14 && daysToExpiry > 0) {
      this.callbacks.onDTEWarning?.(position, daysToExpiry);
    }

    // Update position in database
    await updatePosition(this.clerkId, position.id!, {
      currentPrice: estimatedSpreadPrice,
      currentPnL: pnl,
      currentPnLPercent: pnlPercent,
      trailingStopPrice
    });

    this.callbacks.onPriceUpdate?.(position, estimatedSpreadPrice, pnl, pnlPercent);
  }

  private estimateSpreadPrice(position: ActivePosition, currentStockPrice: number): number {
    // Simplified spread price estimation
    // In production, you'd fetch the actual option chain prices

    const legs = position.legs;
    if (!legs || legs.length === 0) return position.entryPrice;

    // For vertical spreads, estimate based on intrinsic value changes
    // This is a rough approximation
    if (position.creditDebit === 'CREDIT') {
      // Credit spread: profit when spread value decreases
      // Entry credit - current spread value = P&L
      // Simplified: if stock moved in our favor, spread value decreases
      const isBullish = position.direction === 'BULLISH';
      const shortLeg = legs.find(l => l.action === 'SELL');

      if (!shortLeg) return position.entryPrice;

      if (isBullish) {
        // Bull put spread: profit if stock stays above short strike
        if (currentStockPrice > shortLeg.strike) {
          // Spread is OTM, worth less (good for credit spread)
          const distanceAbove = currentStockPrice - shortLeg.strike;
          const decayFactor = Math.min(1, distanceAbove / (shortLeg.strike * 0.05));
          return position.entryPrice * (1 - decayFactor * 0.8);
        } else {
          // Spread is ITM, worth more (bad for credit spread)
          const intrusion = shortLeg.strike - currentStockPrice;
          return position.entryPrice + intrusion;
        }
      } else {
        // Bear call spread: profit if stock stays below short strike
        if (currentStockPrice < shortLeg.strike) {
          const distanceBelow = shortLeg.strike - currentStockPrice;
          const decayFactor = Math.min(1, distanceBelow / (shortLeg.strike * 0.05));
          return position.entryPrice * (1 - decayFactor * 0.8);
        } else {
          const intrusion = currentStockPrice - shortLeg.strike;
          return position.entryPrice + intrusion;
        }
      }
    } else {
      // Debit spread: profit when spread value increases
      const longLeg = legs.find(l => l.action === 'BUY');
      if (!longLeg) return position.entryPrice;

      const isBullish = position.direction === 'BULLISH';

      if (isBullish) {
        // Bull call spread: profit if stock goes above long strike
        if (currentStockPrice > longLeg.strike) {
          const intrinsicGain = currentStockPrice - longLeg.strike;
          return position.entryPrice + intrinsicGain * 0.8; // Rough delta adjustment
        } else {
          // OTM, losing value
          const distance = longLeg.strike - currentStockPrice;
          const lossFactor = Math.min(1, distance / (longLeg.strike * 0.05));
          return Math.max(0, position.entryPrice * (1 - lossFactor * 0.5));
        }
      } else {
        // Bear put spread: profit if stock goes below long strike
        if (currentStockPrice < longLeg.strike) {
          const intrinsicGain = longLeg.strike - currentStockPrice;
          return position.entryPrice + intrinsicGain * 0.8;
        } else {
          const distance = currentStockPrice - longLeg.strike;
          const lossFactor = Math.min(1, distance / (longLeg.strike * 0.05));
          return Math.max(0, position.entryPrice * (1 - lossFactor * 0.5));
        }
      }
    }
  }

  private calculatePnL(position: ActivePosition, currentSpreadPrice: number): number {
    if (position.creditDebit === 'CREDIT') {
      // Credit: collected premium - current spread value
      return (position.entryPrice - currentSpreadPrice) * 100 * position.contracts;
    } else {
      // Debit: current spread value - paid premium
      return (currentSpreadPrice - position.entryPrice) * 100 * position.contracts;
    }
  }

  private isStopHit(position: ActivePosition, currentPrice: number, trailingStop?: number): boolean {
    const stopPrice = trailingStop || position.stopLossPrice;

    if (position.creditDebit === 'CREDIT') {
      // Credit spread: stop hit when spread value exceeds stop (losing money)
      return currentPrice >= stopPrice;
    } else {
      // Debit spread: stop hit when spread value falls below stop
      return currentPrice <= stopPrice;
    }
  }

  private getDaysToExpiry(position: ActivePosition): number {
    if (!position.expirationDate) return 999;
    const exp = new Date(position.expirationDate);
    const now = new Date();
    return Math.ceil((exp.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
  }
}

export const positionPollingService = new PositionPollingService();

// ============================================
// BROWSER NOTIFICATION SERVICE
// ============================================

export class NotificationService {
  private permission: NotificationPermission = 'default';

  async requestPermission(): Promise<boolean> {
    if (!('Notification' in window)) {
      console.warn('[NotificationService] Browser does not support notifications');
      return false;
    }

    if (Notification.permission === 'granted') {
      this.permission = 'granted';
      return true;
    }

    if (Notification.permission !== 'denied') {
      const permission = await Notification.requestPermission();
      this.permission = permission;
      return permission === 'granted';
    }

    return false;
  }

  canNotify(): boolean {
    return this.permission === 'granted';
  }

  notify(title: string, options?: NotificationOptions) {
    if (!this.canNotify()) {
      console.warn('[NotificationService] Notifications not permitted');
      return;
    }

    const notification = new Notification(title, {
      icon: '/favicon.ico',
      badge: '/favicon.ico',
      ...options
    });

    // Auto-close after 10 seconds
    setTimeout(() => notification.close(), 10000);

    return notification;
  }

  notifyStopHit(position: ActivePosition, currentPrice: number) {
    this.notify(`STOP HIT: ${position.ticker}`, {
      body: `${position.strategyName} hit stop at $${currentPrice.toFixed(2)}`,
      tag: `stop-${position.id}`,
      requireInteraction: true
    });
  }

  notifyProfitTarget(position: ActivePosition, targetPercent: 50 | 100, currentPrice: number) {
    this.notify(`PROFIT TARGET: ${position.ticker}`, {
      body: `${position.strategyName} reached ${targetPercent}% profit target`,
      tag: `profit-${position.id}-${targetPercent}`,
      requireInteraction: true
    });
  }

  notifyDTEWarning(position: ActivePosition, daysRemaining: number) {
    this.notify(`DTE WARNING: ${position.ticker}`, {
      body: `${position.strategyName} has ${daysRemaining} days until expiration`,
      tag: `dte-${position.id}`,
      requireInteraction: false
    });
  }
}

export const notificationService = new NotificationService();

// ============================================
// POSITION TRACKING HOOK HELPERS
// ============================================

export function createPositionFromAnalysis(
  analysis: any,
  strategy: any,
  tradePlan: any
): Partial<ActivePosition> {
  // Find expiration from legs
  const expiration = strategy.legs[0]?.expiry || '';

  // Calculate profit targets for credit vs debit
  let profitTarget50: number;
  let profitTarget100: number;

  if (strategy.creditDebit === 'CREDIT') {
    // Credit spread: 50% profit = half the credit retained
    profitTarget50 = strategy.legs.reduce((sum: number, l: any) =>
      l.action === 'SELL' ? sum + l.mid : sum - l.mid, 0) * 0.5;
    profitTarget100 = 0; // Full profit when spread goes to 0
  } else {
    // Debit spread: targets based on max profit
    profitTarget50 = tradePlan.entryPrice + (strategy.maxProfit / 100) * 0.5;
    profitTarget100 = tradePlan.entryPrice + (strategy.maxProfit / 100);
  }

  return {
    ticker: analysis.ticker,
    strategyName: strategy.name,
    strategyType: strategy.type,
    direction: strategy.direction,
    creditDebit: strategy.creditDebit,
    legs: strategy.legs,
    contracts: tradePlan.contracts,
    entryPrice: tradePlan.entryPrice,
    entryDate: new Date().toISOString(),
    expirationDate: expiration,
    maxProfit: strategy.maxProfit,
    maxLoss: strategy.maxLoss,
    breakeven: strategy.breakeven,
    stopLossPrice: typeof tradePlan.stopLoss === 'number'
      ? tradePlan.stopLoss
      : tradePlan.stopLoss?.price || tradePlan.entryPrice * 2,
    profitTarget50,
    profitTarget100,
    atrValue: analysis.technicals?.atr,
    trailingStopEnabled: false,
    alertsEnabled: true,
    emailAlertsEnabled: false,
    alertCooldownMinutes: 30,
    status: 'ACTIVE'
  };
}
